import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import partyService from "../services/partyService";
import { toast } from "react-toastify";

/**
 * Create a new party
 */
export const createParty = createAsyncThunk(
  "party/create",
  async (partyData, { rejectWithValue }) => {
    try {
      const response = await partyService.createParty(partyData);
      toast.success("Party created successfully");
      return response.party;
    } catch (error) {
      toast.error(error.message || "Failed to create party");
      return rejectWithValue(error.message || "Failed to create party");
    }
  }
);

/**
 * Get all parties
 */
export const getAllParties = createAsyncThunk(
  "party/getAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await partyService.getAllParties(params);
      return response;
    } catch (error) {
      toast.error(error.message || "Failed to fetch parties");
      return rejectWithValue(error.message || "Failed to fetch parties");
    }
  }
);

/**
 * Get single party by ID
 */
export const getPartyById = createAsyncThunk(
  "party/getById",
  async (partyId, { rejectWithValue }) => {
    try {
      const response = await partyService.getPartyById(partyId);
      return response.party;
    } catch (error) {
      toast.error(error.message || "Failed to fetch party");
      return rejectWithValue(error.message || "Failed to fetch party");
    }
  }
);

/**
 * Update party
 */
export const updateParty = createAsyncThunk(
  "party/update",
  async ({ partyId, partyData }, { rejectWithValue }) => {
    try {
      const response = await partyService.updateParty(partyId, partyData);
      toast.success("Party updated successfully");
      return response.party;
    } catch (error) {
      toast.error(error.message || "Failed to update party");
      return rejectWithValue(error.message || "Failed to update party");
    }
  }
);

/**
 * Delete party
 */
export const deleteParty = createAsyncThunk(
  "party/delete",
  async (partyId, { rejectWithValue }) => {
    try {
      await partyService.deleteParty(partyId);
      toast.success("Party deleted successfully");
      return partyId;
    } catch (error) {
      toast.error(error.message || "Failed to delete party");
      return rejectWithValue(error.message || "Failed to delete party");
    }
  }
);

/**
 * Get active parties
 */
export const getActiveParties = createAsyncThunk(
  "party/getActive",
  async (_, { rejectWithValue }) => {
    try {
      const response = await partyService.getActiveParties();
      return response.parties;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch active parties");
    }
  }
);

// Initial state
const initialState = {
  parties: [],
  currentParty: null,
  activeParties: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalParties: 0,
  },
  loading: false,
  error: null,
  createLoading: false,
  createSuccess: false,
  updateLoading: false,
  updateSuccess: false,
  activePartiesLoading: false,
};

// Slice
const partySlice = createSlice({
  name: "party",
  initialState,
  reducers: {
    clearCurrentParty: (state) => {
      state.currentParty = null;
    },
    resetCreate: (state) => {
      state.createSuccess = false;
    },
    resetUpdate: (state) => {
      state.updateSuccess = false;
    },
    clearErrors: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Party
      .addCase(createParty.pending, (state) => {
        state.createLoading = true;
        state.createSuccess = false;
        state.error = null;
      })
      .addCase(createParty.fulfilled, (state, action) => {
        state.createLoading = false;
        state.createSuccess = true;
        state.parties = [action.payload, ...state.parties];
        state.error = null;
      })
      .addCase(createParty.rejected, (state, action) => {
        state.createLoading = false;
        state.createSuccess = false;
        state.error = action.payload;
      })
      // Get All Parties
      .addCase(getAllParties.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllParties.fulfilled, (state, action) => {
        state.loading = false;
        state.parties = action.payload.parties;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllParties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Party By ID
      .addCase(getPartyById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPartyById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentParty = action.payload;
        state.error = null;
      })
      .addCase(getPartyById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Party
      .addCase(updateParty.pending, (state) => {
        state.updateLoading = true;
        state.updateSuccess = false;
        state.error = null;
      })
      .addCase(updateParty.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;
        state.parties = state.parties.map((party) =>
          party._id === action.payload._id ? action.payload : party
        );
        state.currentParty = action.payload;
        state.error = null;
      })
      .addCase(updateParty.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = false;
        state.error = action.payload;
      })
      // Delete Party
      .addCase(deleteParty.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteParty.fulfilled, (state, action) => {
        state.loading = false;
        state.parties = state.parties.filter(
          (party) => party._id !== action.payload
        );
        state.error = null;
      })
      .addCase(deleteParty.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Active Parties
      .addCase(getActiveParties.pending, (state) => {
        state.activePartiesLoading = true;
        state.error = null;
      })
      .addCase(getActiveParties.fulfilled, (state, action) => {
        state.activePartiesLoading = false;
        state.activeParties = action.payload;
        state.error = null;
      })
      .addCase(getActiveParties.rejected, (state, action) => {
        state.activePartiesLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentParty, resetCreate, resetUpdate, clearErrors } =
  partySlice.actions;
export default partySlice.reducer;

